import { Label, p50, percent, time } from "@amcharts/amcharts5";
import {
  AxisRenderer,
  AxisRendererX,
  AxisRendererY,
  CategoryAxis,
  ColumnSeries,
  DateAxis,
  ValueAxis,
  XYChart,
  XYCursor,
} from "@amcharts/amcharts5/xy";
import { forIn, orderBy } from "lodash";

import BaseChart, { ChartOptions, LegendData } from "@/classes/BaseChart";
import { TIME_UNIT, getChartInterval } from "@/helpers/charts";
import { stringOrNumberToDate } from "@/helpers/formatters";
import { axisTitle } from "@/settings/charts";
import { FAMILY } from "@/settings/widgetFamilies";
import { chartFontSettings, chartInitialSettings, getAxisDateFormat } from "@/settings/widgets";
import { ChartDataRecord, TooltipSeries } from "@/types/charts";

export default class BarChart extends BaseChart {
  isDateAxis!: boolean;
  interval!: time.ITimeInterval;

  public loadChart(chartName: string, family: string): void {
    super.loadChart(chartName, family);
    this.isDateAxis = this.chartFamily === FAMILY.OVERTIME;
  }

  initChart() {
    this.root.container.children.clear();

    if (this.chart) {
      (this.chart as XYChart).series.clear();
    }

    if (this.isDateAxis) {
      this.interval = getChartInterval(this.settings.yAxis) as time.ITimeInterval;
    }

    this.createChart({});
    this.createRenderers();
    this.createAxes();
    this.createCursor();

    let legendNames: string[] = [];

    const series = this.isDateAxis ? [this.settings.xAxisPrimary] : this.data;

    series.reverse().forEach((el: ChartDataRecord, index: number) => {
      const paletteLength = this.palette.length;
      const indexColor = index % paletteLength;
      this.isDateAxis ? legendNames.push(el[this.settings.yAxis] as string) : (legendNames = series);
      this.createBarSeries(el, indexColor);
    });

    this.showLegend(legendNames, (this.chart as XYChart).series.values as LegendData);
  }

  createChart(chartOptions: ChartOptions) {
    this.chart = this.root.container.children.push(
      XYChart.new(this.root, {
        ...chartInitialSettings,
        paddingTop: chartOptions.noTopPadding ? 0 : 10,
        paddingRight: 20,
        panX: false,
        panY: false,
        wheelX: "panX",
        wheelY: "zoomX",
        layout: this.layout,
        focusable: true,
      })
    );
  }

  createRenderers() {
    const y = AxisRendererY.new(this.root, {});
    const x = AxisRendererX.new(this.root, {});

    y.labels.template.setAll({ ...chartFontSettings.label });
    x.labels.template.setAll({ ...chartFontSettings.label });

    this.renderers = { y, x };
  }

  createCursor() {
    const cursor = (this.chart as XYChart).set(
      "cursor",
      XYCursor.new(this.root, {
        behavior: "zoomX",
      })
    );
    cursor.lineX.set("visible", false);
  }

  createAxes() {
    const y = this.isDateAxis
      ? (this.chart as XYChart).yAxes.push(
          DateAxis.new(this.root, {
            renderer: this.renderers.y as AxisRendererY,
            groupData: true,
            groupIntervals: [this.interval],
            markUnitChange: false,
            baseInterval: { timeUnit: TIME_UNIT.DAY, count: 1 },
            dateFormats: getAxisDateFormat(this.settings.yAxis),
            marginRight: 14,
          })
        )
      : (this.chart as XYChart).yAxes.push(
          CategoryAxis.new(this.root, {
            categoryField: this.settings.yAxis,
            renderer: this.renderers.y as AxisRendererY,
            marginRight: 14,
          })
        );

    y.data.setAll(this.data);

    const xPrimary = (this.chart as XYChart).xAxes.push(
      ValueAxis.new(this.root, {
        renderer: this.renderers.x as AxisRenderer,
        min: 0,
        marginTop: 14,
        numberFormat: this.getCompactAxesMetricFormatter(this.settings.xAxisPrimary, this.data),
      })
    );

    if (this.settings.xAxisPrimaryTitle) {
      xPrimary.children.push(
        Label.new(this.root, {
          text: this.settings.xAxisPrimaryTitle,
          ...chartFontSettings.axisTitle,
          x: p50,
          marginTop: axisTitle.marginTop,
          centerX: p50,
        })
      );
    }

    if (this.settings.yAxisTitle) {
      y.children.unshift(
        Label.new(this.root, {
          text: this.settings.yAxisTitle,
          ...chartFontSettings.axisTitle,
          rotation: -90,
          y: percent(50),
          centerX: p50,
        })
      );
    }

    this.axes = { xPrimary, y };
  }

  createBarSeries(item: ChartDataRecord, index: number) {
    const seriesConfig = {
      name: this.isDateAxis ? this.settings.xAxisPrimary : (item[this.settings.yAxis] as string),
      xAxis: this.axes.xPrimary,
      yAxis: this.axes.y,
      valueXField: this.settings.xAxisPrimary,
      [this.isDateAxis ? "valueYField" : "categoryYField"]: this.settings.yAxis,
      fill: this.getSeriesColor(index),
    };

    if (!this.isDateAxis) {
      seriesConfig.clustered = false;
    }

    const series: ColumnSeries = (this.chart as XYChart).series.push(ColumnSeries.new(this.root, seriesConfig));
    // Use this for future data-label tasks
    //
    //   series.bullets.push((_root, _series, dataItem) => {
    //   const label = Label.new(this.root, {
    //     text: "{valueX.formatNumber('#,##0')}",
    //     populateText: true,
    //     centerY: p50,
    //     x: percent(100),
    //     dx: 8,
    //   });
    //
    //   label.adapters.add("visible", () => {
    //     const v = dataItem?.get("valueX");
    //     return typeof v === "number" ? v > 0 : !!v;
    //   });
    //
    //   return Bullet.new(this.root, {
    //     locationX: 1,
    //     sprite: label,
    //   });
    // });

    this.createTooltip(series as TooltipSeries, () => {
      const param: Record<string, unknown>[] = [];
      const tooltipValues = series.get("tooltip")?.dataItem?.dataContext as Record<string, unknown>;
      if (tooltipValues) {
        forIn(tooltipValues, (value, key) => {
          if (this.isDateAxis || series.get("valueXField") === key || series.get("categoryYField") === key) {
            param.push({ key, value });
          }
        });
      }
      return param;
    });

    if (this.isDateAxis) {
      this.createLegendLabel(series, this.settings.xAxisPrimary);
      this.updateBarSeriesData(this.settings.xAxisPrimary, series);
    } else {
      series.data.push(item);
    }
  }

  updateBarSeriesData(seriesName: string, series: ColumnSeries): void {
    const data = this.isDateAxis
      ? this.data.map((el: ChartDataRecord) => ({
          [this.settings.yAxis]: stringOrNumberToDate(el[this.settings.yAxis] as string).getTime(),
          [seriesName]: el[seriesName],
        }))
      : this.data;

    series.data.setAll(data);
  }

  mapData(data: ChartDataRecord[]) {
    data = data.map((item) => ({
      [this.settings.xAxisPrimary]: item[this.settings.xAxisPrimary] ?? 0,
      [this.settings.yAxis]: item[this.settings.yAxis] ?? 0,
    }));

    if (!this.isDateAxis) {
      data = orderBy(data, [this.settings.xAxisPrimary], ["asc"]) as ChartDataRecord[];
      data = this.showItems(data as ChartDataRecord[], this.settings.yAxis, this.settings.xAxisPrimary, "first");
    }
    return data;
  }
}
